import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

import type { Exception } from './exceptions'
import type { Receipt } from './receipts'

const api = clientFor('/pod/api')

/** 运单状态机（services/pod/internal/shipment/model.go 的注释与常量同源） */
export type ShipmentStatus =
  | 'created'
  | 'in_transit'
  | 'arrived'
  | 'signed'
  | 'exception'
  | 'closed'

/** 可在页面上触发的运单动作，与后端 POST /shipments/:id/<action> 一一对应 */
export type ShipmentAction = 'load' | 'depart' | 'arrive' | 'sign' | 'close'

/** 签收结果：signed/partial 不改运单状态，refused/damaged 会同事务建异常并把运单置 exception */
export type SignResult = 'signed' | 'partial' | 'refused' | 'damaged'

/** 追溯事件类型（append-only，与后端 internal/trace/model.go 的 TypeXxx 一一对应） */
export type TraceEventType =
  | 'created'
  | 'loaded'
  | 'departed'
  | 'arrived'
  | 'signed'
  | 'refused'
  | 'damaged'
  | 'exception'
  | 'closed'

/** 一票运单。字段与 services/pod/internal/shipment/model.go 的 Shipment 一一对应。 */
export interface Shipment {
  id: number
  tenant_id: number
  /** 运单号，前缀 YD */
  doc_no: string
  /** 引用 dsp 行车任务号（纯字符串，不跨库校验） */
  trip_no: string
  /** 引用 ops 订单号（纯字符串，不跨库校验） */
  order_no: string
  carrier: string
  origin: string
  dest: string
  cargo_desc: string
  qty: number
  weight_kg: number
  depart_at: string | null
  arrive_at: string | null
  signed_at: string | null
  status: ShipmentStatus
  remark: string
  created_at: string
  updated_at: string
}

/** 列表行：运单 + 已签收件数 + 未关闭异常数（后端用相关子查询带出） */
export interface ShipmentRow extends Shipment {
  signed_qty: number
  open_exceptions: number
}

/** 详情与列表行同构；四个区块由 GET /shipments/:id/trace 一次性给出 */
export type ShipmentDetail = ShipmentRow

/** 一条电子签收记录：一次运单可多次签收（部分签收），签满才置 signed */
export interface PodRecord {
  id: number
  tenant_id: number
  shipment_id: number
  signee: string
  sign_phone: string
  sign_at: string
  result: SignResult
  signed_qty: number
  location: string
  remark: string
  operator: string
  created_at: string
  updated_at: string
}

/** 时间线上的一条事件：谁在什么时候、在哪里、对运单做了什么 */
export interface TraceEvent {
  id: number
  tenant_id: number
  shipment_id: number
  event_type: TraceEventType
  location: string
  detail: string
  operator: string
  happened_at: string
  created_at: string
  updated_at: string
}

/** GET /shipments/:id/trace 的一次性返回：运单 + 时间线 + 签收记录 + 回单 + 异常 */
export interface TraceBundle {
  shipment: Shipment
  events: TraceEvent[]
  pod_records: PodRecord[]
  receipts: Receipt[]
  exceptions: Exception[]
}

/**
 * 新建 / 编辑运单的入参：起止地与件数必填（后端 validateInput 校验），外部单号可选。
 * expect_deliver_at 是页面要求的「期望送达时间」，但后端 shipment.Input 与 shipments 表
 * 都没有这一列（只有 depart_at/arrive_at/signed_at，由动作写入），Go 的 JSON 绑定会忽略
 * 未知字段——提交不报错但不会入库，属已知契约出入。
 */
export interface ShipmentInput {
  trip_no?: string
  order_no?: string
  carrier?: string
  origin: string
  dest: string
  cargo_desc?: string
  qty: number
  weight_kg?: number
  remark?: string
  expect_deliver_at?: string
}

/** 装车 / 发运 / 到达 / 关闭的入参：都可留空（后端 bindOptional 放行空 body） */
export interface ShipmentActionInput {
  location?: string
  remark?: string
}

/** 电子签收入参：signee 必填，signed_qty 必须大于 0 且不超过剩余可签数量 */
export interface SignInput {
  signee: string
  sign_phone?: string
  result: SignResult
  signed_qty: number
  location?: string
  remark?: string
}

/** 签收动作的返回：新落的签收记录 + 最新运单，顺带回单 / 异常单 */
export interface SignResultBundle {
  pod_record: PodRecord
  shipment: Shipment
  receipt?: Receipt
  exception?: Exception
}

export interface ShipmentQuery extends PageQuery {
  /** 单号 / 行车任务号 / 订单号模糊匹配 */
  keyword?: string
  status?: string
  /** 精确匹配 */
  trip_no?: string
  /** 精确匹配 */
  order_no?: string
}

/** 与后端 services/pod/internal/shipment/model.go 的 StatusNames 同源两份，改一边必须改另一边 */
export const STATUS_LABELS: Record<ShipmentStatus, string> = {
  created: '已创建',
  in_transit: '在途',
  arrived: '已到达',
  signed: '已签收',
  exception: '异常',
  closed: '已关闭',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<
  ShipmentStatus,
  'info' | 'primary' | 'warning' | 'success' | 'danger'
> = {
  created: 'info',
  in_transit: 'primary',
  arrived: 'warning',
  signed: 'success',
  exception: 'danger',
  closed: 'info',
}

/** 与后端 services/pod/internal/shipment/model.go 的 ResultNames 同源两份 */
export const SIGN_RESULT_LABELS: Record<SignResult, string> = {
  signed: '签收',
  partial: '部分签收',
  refused: '拒收',
  damaged: '破损',
}

/** 签收结果 → 标签色：拒收 / 破损会建异常，必须一眼看出 */
export const SIGN_RESULT_TAG: Record<SignResult, 'success' | 'warning' | 'danger'> = {
  signed: 'success',
  partial: 'warning',
  refused: 'danger',
  damaged: 'danger',
}

/** 与后端 services/pod/internal/trace/model.go 的 TypeNames 同源两份 */
export const EVENT_LABELS: Record<TraceEventType, string> = {
  created: '创建运单',
  loaded: '装车',
  departed: '发运',
  arrived: '到达',
  signed: '签收',
  refused: '拒收',
  damaged: '破损',
  exception: '转入异常',
  closed: '关闭',
}

/** 事件类型 → 时间线节点色 */
export const EVENT_TAG: Record<TraceEventType, 'info' | 'primary' | 'warning' | 'success' | 'danger'> =
  {
    created: 'info',
    loaded: 'primary',
    departed: 'primary',
    arrived: 'warning',
    signed: 'success',
    refused: 'danger',
    damaged: 'danger',
    exception: 'danger',
    closed: 'info',
  }

/**
 * 各状态允许的动作，抄后端 internal/shipment/service.go：
 * load 只在 created/in_transit；depart 只在 created；arrive 只在 in_transit；
 * sign 排除 signed/closed（其余状态都能签，剩余可签件数由后端再校验）；
 * close 排除 closed 与 exception（exception 必然还挂着未关闭异常，后端会 409）。
 * 前端只做展示，最终以后端 409 为准。
 */
const ACTIONS: Record<ShipmentStatus, ShipmentAction[]> = {
  created: ['load', 'depart', 'sign', 'close'],
  in_transit: ['load', 'arrive', 'sign', 'close'],
  arrived: ['sign', 'close'],
  signed: ['close'],
  exception: ['sign'],
  closed: [],
}

/** 状态能不能触发某个动作 */
export function canAct(status: ShipmentStatus, action: ShipmentAction): boolean {
  return ACTIONS[status].includes(action)
}

/** 剩余可签件数：总量减累计签收量，签收表单的上限就是它 */
export function remainingQty(shipment: Shipment, records: PodRecord[]): number {
  const signed = records.reduce((sum, r) => sum + r.signed_qty, 0)
  return Math.max(shipment.qty - signed, 0)
}

export const listShipments = (q: ShipmentQuery): Promise<PageResult<ShipmentRow>> =>
  api.get<PageResult<ShipmentRow>>('/shipments', { ...q })

export const getShipment = (id: number): Promise<ShipmentDetail> =>
  api.get<ShipmentDetail>(`/shipments/${id}`)

/** 追溯：运单 + 事件时间线 + 签收记录 + 回单 + 异常，一次性带回 */
export const traceShipment = (id: number): Promise<TraceBundle> =>
  api.get<TraceBundle>(`/shipments/${id}/trace`)

export const createShipment = (input: ShipmentInput): Promise<Shipment> =>
  api.post<Shipment>('/shipments', input)

export const updateShipment = (id: number, input: ShipmentInput): Promise<Shipment> =>
  api.put<Shipment>(`/shipments/${id}`, input)

const act = (id: number, action: string, input?: ShipmentActionInput): Promise<Shipment> =>
  api.post<Shipment>(`/shipments/${id}/${action}`, input ?? {})

export const loadShipment = (id: number, input?: ShipmentActionInput): Promise<Shipment> =>
  act(id, 'load', input)

export const departShipment = (id: number, input?: ShipmentActionInput): Promise<Shipment> =>
  act(id, 'depart', input)

export const arriveShipment = (id: number, input?: ShipmentActionInput): Promise<Shipment> =>
  act(id, 'arrive', input)

export const closeShipment = (id: number, input?: ShipmentActionInput): Promise<Shipment> =>
  act(id, 'close', input)

export const signShipment = (id: number, input: SignInput): Promise<SignResultBundle> =>
  api.post<SignResultBundle>(`/shipments/${id}/sign`, input)

/**
 * 按运单号追溯（全程追溯页入口）：后端 GET /trace?doc_no= 一次性带回整条 bundle，
 * 只给 pod:trace 的账号也能查（不依赖 pod:shipment 的列表检索权限）。
 * 单号不存在时后端 404，照常抛错由页面给错误态。
 */
export const traceByDocNo = (docNo: string): Promise<TraceBundle> =>
  api.get<TraceBundle>('/trace', { doc_no: docNo.trim() })
